const initElementAutoScroll = () => {
  const carousels = document.querySelectorAll('[data-me-auto-scroll]:not([data-me-auto-scroll-ready])');

  carousels.forEach((carousel) => {
    const scroller = carousel.querySelector('slideshow-slides');
    if (!scroller) return;

    carousel.dataset.meAutoScrollReady = 'true';

    const duration = Number(carousel.dataset.meAutoScrollSpeed || 24);
    const pixelsPerFrame = Math.max(0.35, scroller.scrollWidth / Math.max(duration * 60, 1));
    let paused = false;

    carousel.addEventListener('pointerenter', () => {
      paused = true;
    });
    carousel.addEventListener('pointerleave', () => {
      paused = false;
    });
    carousel.addEventListener('focusin', () => {
      paused = true;
    });
    carousel.addEventListener('focusout', () => {
      paused = false;
    });

    const tick = () => {
      if (!paused && scroller.scrollWidth > scroller.clientWidth) {
        const maxScroll = scroller.scrollWidth - scroller.clientWidth - 2;
        scroller.scrollLeft = scroller.scrollLeft >= maxScroll ? 0 : scroller.scrollLeft + pixelsPerFrame;
      }

      requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  });
};

const initHomeSwimAutoScroll = () => {
  document.querySelectorAll('[data-me-home-swim-auto-scroll]:not([data-me-home-swim-auto-ready])').forEach((carousel) => {
    const track = carousel.querySelector('.me-home-swim__track');
    const viewport = carousel.querySelector('.me-home-swim__viewport');
    if (!track) return;

    const originalCards = Array.from(track.children);
    if (originalCards.length < 2) return;

    carousel.dataset.meHomeSwimAutoReady = 'true';

    const cloneOriginalCards = () => {
      originalCards.forEach((card) => {
        const clone = card.cloneNode(true);
        clone.removeAttribute('data-me-tap-active');
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a, button').forEach((interactive) => {
          interactive.setAttribute('tabindex', '-1');
        });
        track.appendChild(clone);
      });
    };

    const getLoopWidth = () => {
      const firstClone = track.children[originalCards.length];
      return firstClone ? firstClone.offsetLeft : track.scrollWidth / 2;
    };

    const fillWideViewports = () => {
      const viewportWidth = viewport?.clientWidth || carousel.clientWidth || 0;
      let loopWidth = getLoopWidth();
      let guard = 0;

      while (loopWidth > 0 && track.scrollWidth < viewportWidth + loopWidth * 2 && guard < 8) {
        cloneOriginalCards();
        loopWidth = getLoopWidth();
        guard += 1;
      }
    };

    cloneOriginalCards();
    fillWideViewports();

    const duration = Number(carousel.dataset.meHomeSwimSpeed || 30);
    const previous = carousel.querySelector('[data-me-home-swim-prev]');
    const next = carousel.querySelector('[data-me-home-swim-next]');
    let paused = false;
    let manualPauseUntil = 0;
    let manualActiveTimer;
    let transitionTimer;
    let offset = 0;
    let lastTimestamp;

    const getStep = () => {
      const card = track.querySelector('.me-home-swim-card');
      if (!card) return carousel.clientWidth;
      const styles = window.getComputedStyle(track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return card.getBoundingClientRect().width + gap;
    };

    const syncActiveCard = () => {
      const bounds = (viewport || carousel).getBoundingClientRect();
      const center = bounds.left + bounds.width / 2;
      let closestCard;
      let closestDistance = Infinity;

      track.querySelectorAll('.me-home-swim-card').forEach((card) => {
        const cardBounds = card.getBoundingClientRect();
        if (!cardBounds.width) return;

        const distance = Math.abs(cardBounds.left + cardBounds.width / 2 - center);
        if (distance < closestDistance) {
          closestCard = card;
          closestDistance = distance;
        }
      });

      track.querySelectorAll('.me-home-swim-card--active').forEach((card) => {
        if (card !== closestCard) card.classList.remove('me-home-swim-card--active');
      });

      closestCard?.classList.add('me-home-swim-card--active');
    };

    const applyOffset = ({ animated = false, normalize = true } = {}) => {
      const loopWidth = getLoopWidth();
      if (!loopWidth) return;

      if (normalize) {
        while (offset >= loopWidth) offset -= loopWidth;
        while (offset < 0) offset += loopWidth;
      }

      window.clearTimeout(transitionTimer);
      track.style.transition = animated ? 'transform 420ms cubic-bezier(0.22, 0.61, 0.36, 1)' : '';
      track.style.transform = `translate3d(${-offset}px, 0, 0)`;

      if (animated) {
        transitionTimer = window.setTimeout(() => {
          track.style.transition = '';
          applyOffset();
        }, 430);
      } else {
        window.requestAnimationFrame(syncActiveCard);
      }
    };

    const showManualMeta = () => {
      carousel.dataset.meHomeSwimManualActive = 'true';
      window.clearTimeout(manualActiveTimer);
      manualActiveTimer = window.setTimeout(() => {
        delete carousel.dataset.meHomeSwimManualActive;
      }, 2200);
    };

    const moveByStep = (direction) => {
      const step = getStep();
      const loopWidth = getLoopWidth();
      if (!step || !loopWidth) return;

      manualPauseUntil = performance.now() + 1800;
      showManualMeta();

      if (direction < 0 && offset <= 1) {
        offset = loopWidth;
        applyOffset({ normalize: false });
        track.getBoundingClientRect();
      }

      offset += step * direction;
      applyOffset({ animated: true, normalize: direction < 0 });
    };

    carousel.addEventListener('pointerenter', () => {
      paused = true;
    });

    carousel.addEventListener('pointerleave', () => {
      paused = false;
    });

    carousel.addEventListener('focusin', () => {
      paused = true;
    });
    carousel.addEventListener('focusout', () => {
      paused = false;
    });

    previous?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      moveByStep(-1);
    });

    next?.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      moveByStep(1);
    });

    window.addEventListener('resize', fillWideViewports, { passive: true });
    syncActiveCard();

    const tick = (timestamp) => {
      const loopWidth = getLoopWidth();
      const delta = lastTimestamp == null ? 0 : timestamp - lastTimestamp;

      lastTimestamp = timestamp;

      if (
        !paused &&
        !carousel.hasAttribute('data-me-home-swim-tap-active') &&
        performance.now() > manualPauseUntil &&
        loopWidth > 0
      ) {
        offset += (loopWidth / Math.max(duration, 1)) * (delta / 1000);
        applyOffset();
      }

      requestAnimationFrame(tick);
    };

    requestAnimationFrame(tick);
  });
};

const initProductCardTapReveal = () => {
  const root = document.documentElement;
  if (root.dataset.meTapRevealReady === 'true') return;

  const cardSelector =
    '.me-home-swim-card, .me-collection-card, .product-recommendations product-card';
  const activeCardSelector =
    ".me-home-swim-card[data-me-tap-active='true'], .me-collection-card[data-me-tap-active='true'], .product-recommendations product-card[data-me-tap-active='true']";
  const touchMemoryDuration = 1400;
  let pendingTouchCard = null;
  let pendingTouchAt = 0;
  let lastTouchPointerAt = Number.NEGATIVE_INFINITY;

  const getCard = (target) => (target instanceof Element ? target.closest(cardSelector) : null);

  const getCardLink = (card, target) => {
    if (!card || !(target instanceof Element)) return null;

    const targetedLink = target.closest('a[href]');
    if (targetedLink && card.contains(targetedLink)) return targetedLink;

    return card.matches('a[href]') ? card : null;
  };

  const prepareProductViewTransition = (card) => {
    if (!card || !document.querySelector("main[data-product-transition='true']")) return;

    const media = card.querySelector('.me-home-swim-card__media, .me-collection-card__media');
    if (!(media instanceof HTMLElement)) return;

    document.querySelectorAll('[data-me-custom-product-transition-source]').forEach((source) => {
      if (source === media) return;
      source.removeAttribute('data-view-transition-triggered');
      source.removeAttribute('data-view-transition-type');
      source.removeAttribute('data-me-custom-product-transition-source');
    });

    media.dataset.viewTransitionType = 'product-image-transition';
    media.dataset.viewTransitionTriggered = 'true';
    media.dataset.meCustomProductTransitionSource = 'true';
  };

  const syncHomeCarouselState = () => {
    document.querySelectorAll('[data-me-home-swim-carousel]').forEach((carousel) => {
      const hasRevealedCard = Boolean(
        carousel.querySelector(".me-home-swim-card[data-me-tap-active='true']")
      );
      carousel.toggleAttribute('data-me-home-swim-tap-active', hasRevealedCard);
    });
  };

  const clearRevealedCards = (exceptCard = null) => {
    let changed = false;

    document.querySelectorAll(activeCardSelector).forEach((card) => {
      if (card === exceptCard) return;

      card.removeAttribute('data-me-tap-active');
      changed = true;

      const focusedElement = document.activeElement;
      if (focusedElement instanceof HTMLElement && card.contains(focusedElement)) {
        focusedElement.blur();
      }
    });

    if (changed) syncHomeCarouselState();
  };

  const rememberTouch = (target) => {
    pendingTouchCard = getCard(target);
    pendingTouchAt = performance.now();
  };

  const activateFinePointer = () => {
    if (root.dataset.meFinePointer === 'true') return;
    root.dataset.meFinePointer = 'true';
    clearRevealedCards();
  };

  if (window.matchMedia?.('(hover: hover) and (pointer: fine)').matches) {
    root.dataset.meFinePointer = 'true';
  }

  document.addEventListener(
    'pointermove',
    (event) => {
      if (event.pointerType === 'pen' && event.buttons === 0 && event.pressure === 0) {
        pendingTouchCard = null;
        pendingTouchAt = 0;
        activateFinePointer();
        return;
      }

      if (
        event.pointerType === 'mouse' &&
        performance.now() - lastTouchPointerAt > touchMemoryDuration
      ) {
        activateFinePointer();
      }
    },
    true
  );

  document.addEventListener(
    'pointerdown',
    (event) => {
      if (event.pointerType === 'mouse' || event.pointerType === 'pen') {
        pendingTouchCard = null;
        pendingTouchAt = 0;
        activateFinePointer();
        return;
      }

      if (
        (event.pointerType !== 'touch' && event.pointerType !== 'pen') ||
        !event.isPrimary ||
        event.button !== 0
      ) {
        return;
      }

      delete root.dataset.meFinePointer;
      lastTouchPointerAt = performance.now();
      rememberTouch(event.target);
    },
    true
  );

  document.addEventListener(
    'pointercancel',
    () => {
      pendingTouchCard = null;
      pendingTouchAt = 0;
    },
    true
  );

  document.addEventListener(
    'touchend',
    (event) => {
      if (event.changedTouches.length !== 1) return;
      delete root.dataset.meFinePointer;
      lastTouchPointerAt = performance.now();
      rememberTouch(event.target);
    },
    { capture: true, passive: true }
  );

  document.addEventListener(
    'click',
    (event) => {
      const card = getCard(event.target);
      const clickPointerType = typeof event.pointerType === 'string' ? event.pointerType : '';
      const hasRecentTouch =
        pendingTouchAt > 0 &&
        performance.now() - pendingTouchAt <= touchMemoryDuration &&
        pendingTouchCard === card;
      const isTouchActivation =
        clickPointerType === 'touch' ||
        (clickPointerType === 'pen' && root.dataset.meFinePointer !== 'true') ||
        hasRecentTouch;

      pendingTouchCard = null;
      pendingTouchAt = 0;

      if (
        event.defaultPrevented ||
        event.detail === 0 ||
        (event.clientX === 0 && event.clientY === 0) ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      if (!card) {
        clearRevealedCards();
        return;
      }

      const link = getCardLink(card, event.target);
      if (!link || (event.target !== link && !link.contains(event.target))) {
        clearRevealedCards();
        return;
      }

      if (!isTouchActivation) {
        prepareProductViewTransition(card);
        return;
      }

      if (card.dataset.meTapActive === 'true') {
        prepareProductViewTransition(card);
        return;
      }

      event.preventDefault();
      clearRevealedCards(card);
      card.dataset.meTapActive = 'true';
      syncHomeCarouselState();
    },
    true
  );

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') clearRevealedCards();
  });

  document.addEventListener('focusin', (event) => {
    const focusedCard = getCard(event.target);
    if (focusedCard?.dataset.meTapActive !== 'true') clearRevealedCards();
  });

  root.dataset.meTapRevealReady = 'true';
};

const initElementCursorPrices = () => {
  const cards = document.querySelectorAll(
    '.product-card:not([data-me-cursor-ready]), .me-untamed-card:not([data-me-cursor-ready])'
  );

  cards.forEach((card) => {
    card.dataset.meCursorReady = 'true';
    let frame = 0;
    let nextX = 0;
    let nextY = 0;
    let bounds = null;
    let label = null;
    let labelWidth = 0;

    const getLabel = () =>
      card.querySelector('.me-product-media-meta, .me-product-hover-overlay__inner');

    const commitCursor = () => {
      frame = 0;
      if (!bounds || !label) return;

      const xInset = Math.min(Math.max(labelWidth / 2, 72), bounds.width / 2);
      const minX = Math.min(xInset, bounds.width / 2);
      const maxX = Math.max(minX, bounds.width - minX);
      const minY = Math.min(68, bounds.height / 2);
      const maxY = Math.max(minY, bounds.height - 32);
      const x = Math.min(Math.max(nextX, minX), maxX);
      const y = Math.min(Math.max(nextY, minY), maxY);

      label.style.transform = `translate3d(${x}px, ${y}px, 0) translate(-50%, -116%)`;
    };

    const updateCursor = (event) => {
      bounds = bounds || card.getBoundingClientRect();
      nextX = event.clientX - bounds.left;
      nextY = event.clientY - bounds.top;

      if (!frame) {
        frame = requestAnimationFrame(commitCursor);
      }
    };

    card.addEventListener('pointerenter', (event) => {
      label = getLabel();
      bounds = card.getBoundingClientRect();
      labelWidth = label?.offsetWidth || 0;
      updateCursor(event);
    });

    card.addEventListener('pointermove', updateCursor);

    card.addEventListener('pointerleave', () => {
      if (frame) {
        cancelAnimationFrame(frame);
        frame = 0;
      }
      bounds = null;
      label?.style.removeProperty('transform');
      label = null;
      labelWidth = 0;
    });
  });
};

const initUntamedCarousel = () => {
  document.querySelectorAll('[data-me-untamed-carousel]:not([data-me-untamed-ready])').forEach((carousel) => {
    const track = carousel.querySelector('.me-untamed-showcase__track');
    const previous = carousel.querySelector('[data-me-untamed-prev]');
    const next = carousel.querySelector('[data-me-untamed-next]');
    if (!track || !previous || !next) return;

    carousel.dataset.meUntamedReady = 'true';

    const getStep = () => {
      const card = track.querySelector('.me-untamed-card');
      if (!card) return track.clientWidth;
      const styles = window.getComputedStyle(track);
      const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
      return card.getBoundingClientRect().width + gap;
    };

    previous.addEventListener('click', () => {
      track.scrollBy({ left: -getStep(), behavior: 'smooth' });
    });

    next.addEventListener('click', () => {
      const maxScroll = track.scrollWidth - track.clientWidth - 2;
      if (track.scrollLeft >= maxScroll) {
        track.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        track.scrollBy({ left: getStep(), behavior: 'smooth' });
      }
    });
  });
};

const initAccountBackdrop = () => {
  const root = document.documentElement;

  document.querySelectorAll('shopify-account:not([data-me-backdrop-bound])').forEach((account) => {
    account.dataset.meBackdropBound = 'true';
    account.addEventListener('open', () => {
      root.dataset.meAccountOpen = 'true';
    });
    account.addEventListener('close', () => {
      delete root.dataset.meAccountOpen;
    });
  });
};

let elementInstafeedObserver;
let elementInstafeedContentObserver;

const hydrateElementInstafeedTrack = (section, feed) => {
  const sourceImage = feed.querySelector('.ifs-slide .ifs-img, .ifs-slide img');
  const slots = section.querySelectorAll('[data-me-instafeed-live]');

  if (!sourceImage || !slots.length) return false;

  const sourceUrl = sourceImage.currentSrc || sourceImage.src;
  if (!sourceUrl) return false;

  slots.forEach((slot, index) => {
    if (slot.dataset.meInstafeedSource === sourceUrl) return;

    const image = sourceImage.cloneNode(false);
    image.className = 'me-social-scroll__live-image';
    image.alt = sourceImage.alt || 'Missing Element on Instagram';
    image.loading = index === 0 ? 'eager' : 'lazy';
    image.decoding = 'async';

    slot.replaceChildren(image);
    slot.dataset.meInstafeedSource = sourceUrl;
    if (index > 0) slot.setAttribute('aria-hidden', 'true');
  });

  section.dataset.meInstafeedReady = 'true';
  return true;
};

const initElementInstafeed = (event) => {
  const eventTarget = event?.target instanceof Element ? event.target : null;
  const section =
    eventTarget?.matches('.me-social-scroll')
      ? eventTarget
      : eventTarget?.querySelector('.me-social-scroll') || document.querySelector('.me-social-scroll');
  const mount = section?.querySelector('[data-me-instafeed-mount]');
  const feed = document.querySelector('#instafeed');

  if (!section || !mount) return;

  if (feed) {
    if (!mount.contains(feed)) mount.appendChild(feed);

    hydrateElementInstafeedTrack(section, feed);

    elementInstafeedContentObserver?.disconnect();
    elementInstafeedContentObserver = new MutationObserver(() => {
      hydrateElementInstafeedTrack(section, feed);
    });
    elementInstafeedContentObserver.observe(feed, {
      attributes: true,
      attributeFilter: ['href', 'src', 'srcset'],
      childList: true,
      subtree: true,
    });

    elementInstafeedObserver?.disconnect();
    elementInstafeedObserver = undefined;
    return;
  }

  if (elementInstafeedObserver) return;

  const main = document.querySelector('main');
  if (!main) return;

  elementInstafeedObserver = new MutationObserver(initElementInstafeed);
  elementInstafeedObserver.observe(main, { childList: true, subtree: true });
};

const unloadElementInstafeed = (event) => {
  const section = event?.target instanceof Element ? event.target.querySelector('.me-social-scroll') : null;
  const feed = section?.querySelector('#instafeed');
  const main = document.querySelector('main');

  if (feed && main) main.appendChild(feed);
  elementInstafeedContentObserver?.disconnect();
  elementInstafeedContentObserver = undefined;
  elementInstafeedObserver?.disconnect();
  elementInstafeedObserver = undefined;
};

initElementAutoScroll();
initHomeSwimAutoScroll();
initProductCardTapReveal();
initElementCursorPrices();
initUntamedCarousel();
initAccountBackdrop();
initElementInstafeed();
document.addEventListener('shopify:section:load', initElementAutoScroll);
document.addEventListener('shopify:section:load', initHomeSwimAutoScroll);
document.addEventListener('shopify:section:load', initAccountBackdrop);
document.addEventListener('shopify:section:load', initElementCursorPrices);
document.addEventListener('shopify:section:load', initUntamedCarousel);
document.addEventListener('shopify:section:load', initElementInstafeed);
document.addEventListener('shopify:section:unload', unloadElementInstafeed);
